import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { cn } from '../lib/classnames.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';

gsap.registerPlugin(ScrollTrigger);

export interface GalleryImage {
  url: string;
  /** width / height. */
  aspect: number;
}

/**
 * ScrollGallery — the signature scroll moment. As you scroll DOWN, the section
 * locks and the gilded works sweep SIDEWAYS (pinned horizontal scroll), the row
 * SKEWING with your scroll velocity so it leans into the motion, then releases
 * back to the vertical page. Buttery on Lenis, GPU-only transforms.
 *
 * Reduced-motion falls back to a clean, swipeable horizontal row.
 */
export function ScrollGallery({
  images,
  className,
}: {
  images: GalleryImage[];
  className?: string;
}) {
  const reduced = useReducedMotion();
  const sectionRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Pad small sets so the sweep has length and never looks thin.
  let cards = images;
  while (cards.length > 0 && cards.length < 7) cards = [...cards, ...images];

  useLayoutEffect(() => {
    if (reduced || cards.length === 0) return;
    const section = sectionRef.current;
    const pin = pinRef.current;
    const track = trackRef.current;
    if (!section || !pin || !track) return;

    const ctx = gsap.context(() => {
      const distance = (): number => Math.max(0, track.scrollWidth - pin.clientWidth);
      const skewTo = gsap.quickTo(track, 'skewX', { duration: 0.5, ease: 'power3' });

      gsap.to(track, {
        x: () => -distance(),
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: () => '+=' + distance(),
          pin,
          scrub: 1,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const v = gsap.utils.clamp(-9, 9, self.getVelocity() / -380);
            skewTo(v);
          },
        },
      });
    }, section);

    // The page-enter wrapper holds a transform for 360ms on mount; recompute pin
    // start/end once it has cleared (and after fonts/layout settle).
    const refresh = window.setTimeout(() => ScrollTrigger.refresh(), 500);

    return () => {
      window.clearTimeout(refresh);
      ctx.revert();
    };
  }, [reduced, cards.length]);

  if (cards.length === 0) return null;

  // --- reduced-motion: a plain, swipeable row ------------------------------
  if (reduced) {
    return (
      <section className={cn('w-full overflow-x-auto', className)}>
        <div className="flex w-max items-center gap-8 px-[8vw] py-10">
          {cards.map((img, i) => (
            <Card key={i} img={img} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className={cn('relative', className)}>
      <div ref={pinRef} className="relative flex h-screen items-center overflow-hidden">
        {/* edge feather so the row dissolves into the page, never a hard wall */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[10vw] bg-gradient-to-r from-surface to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[10vw] bg-gradient-to-l from-surface to-transparent"
        />
        <div ref={trackRef} className="flex items-center gap-[4vw] px-[10vw] will-change-transform">
          {cards.map((img, i) => (
            <Card key={i} img={img} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Card({ img }: { img: GalleryImage }) {
  return (
    <figure
      className="group relative h-[58vh] max-h-[40rem] shrink-0 overflow-hidden rounded-xl bg-surface-sunken"
      style={{
        aspectRatio: String(img.aspect > 0 ? img.aspect : 1),
        boxShadow: '0 40px 90px -40px rgba(0,0,0,0.92)',
      }}
    >
      <img
        src={img.url}
        alt=""
        loading="lazy"
        draggable={false}
        className="h-full w-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-[1.05]"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-accent/35 transition-all duration-300 group-hover:ring-accent/75"
      />
    </figure>
  );
}
